import { Box, Button, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { RiCloseLine } from "react-icons/ri";
import { PersonNameLink } from "src/components/Navigation/PersonNameLink";
import type { ParentSummary } from "src/generated/hooks/form-service/requests";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { formatUSPhoneNumber } from "src/services/format";
import * as Url from "src/services/url";

interface SelectedParentChipProps {
  // An existing parent chosen from the possible-duplicates panel. Shown read-only
  // in place of the create form; its id links the students being added.
  parent: ParentSummary;
  onClear: () => void;
}

export function SelectedParentChip({
  parent,
  onClear,
}: SelectedParentChipProps) {
  const organization = useOrganization();
  const { glossaryTransform } = useGlossary();
  const fullName = [parent.first_name, parent.last_name]
    .filter(Boolean)
    .join(" ");
  const contactParts = [
    parent.phone_number ? formatUSPhoneNumber(parent.phone_number) : null,
    parent.email_address,
  ].filter(Boolean);

  return (
    <Flex
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      padding={4}
      gap={2.5}
      align="flex-start"
    >
      <Box flex="1">
        <Text fontSize="md" fontWeight="700">
          {fullName}
        </Text>
        {contactParts.length > 0 && (
          <Text fontSize="sm" color="gray.700">
            {contactParts.join(" · ")}
          </Text>
        )}
        {parent.children.length > 0 && (
          <Text fontSize="sm" color="gray.700">
            Parent of{" "}
            {parent.children.map((child, index) => (
              <React.Fragment key={child.id}>
                {index > 0 && ", "}
                <PersonNameLink
                  to={Url.OrgAdmin.Students.edit(organization, child.id)}
                >
                  {[child.first_name, child.last_name]
                    .filter(Boolean)
                    .join(" ")}
                </PersonNameLink>
              </React.Fragment>
            ))}
          </Text>
        )}
        <Text fontSize="sm" color="gray.700">
          ID: {parent.id}
        </Text>
      </Box>
      <Button
        type="button"
        variant="outline"
        colorScheme="gray"
        size="sm"
        p="0"
        onClick={onClear}
        aria-label={glossaryTransform("Clear selected parent/guardian")}
      >
        <RiCloseLine />
      </Button>
    </Flex>
  );
}
